import { captureException } from "@sentry/react";
import axios from "axios";
import { ZodError, z } from "zod";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { UserInformation } from "./signin";

/** Softpoint / processor iframe gateway base URL (same host as softpoint-pay script). */
export function getProcessorIframeBaseUrl(): string {
	const apiUrl = import.meta.env.VITE_API_BASE_URL;

	const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
	const isLocalhost =
		typeof window !== "undefined" && localHosts.has(window.location.hostname);

	if (apiUrl.includes("dev") || isLocalhost) {
		return "https://devapi-gtw.tabpoint.us";
	}
	if (apiUrl.includes("stage")) {
		return "https://stage-gtw.softpoint.io";
	}
	if (apiUrl.includes("sandbox")) {
		return "https://sandbox-gtw.softpoint.io";
	}

	return "https://api-gtw.softpoint.io";
}

export type CreateAchBankLinkingSessionParams = {
	locationId: number;
	accessToken: string;
	redirectUri: string;
	user: UserInformation;
	companyName: string;
};

export type CreateAchBankLinkingSessionResult = {
	session_id: number;
	hosted_link_url: string;
};

const SessionResponseSchema = z.object({
	session_id: z.number(),
	hosted_link_url: z.string().min(1),
});

/** US ZIP+4 (e.g. 30087-6042) → five-digit ZIP for processor payloads. */
export function normalizeUsZip5(zip: string | undefined | null): string {
	const z = (zip ?? "").trim();
	return z.length > 5 ? z.slice(0, 5) : z;
}

/**
 * Starts a Plaid (or hosted) bank-linking session on the processor gateway.
 * POST /api/locations/:locationId/ach/bank-linking/session
 *
 * Uses axios directly (not `axiosInstance`) so request interceptors do not merge
 * `user_id` / `global_on_id` into the JSON body.
 */
export async function createAchBankLinkingSession(
	params: CreateAchBankLinkingSessionParams,
): Promise<CreateAchBankLinkingSessionResult> {
	const base = getProcessorIframeBaseUrl();
	const url = `${base}/api/locations/${params.locationId}/ach/bank-linking/session`;
	const { user } = params;
	const sessionPayload = {
		access_token: params.accessToken,
		location_id: params.locationId,
		user_id: user.id,
		country_iso: "US",
		currency_iso: "USD",
		client_url: params.redirectUri,
		customerFirstName: user.first_name,
		customerLastName: user.last_name,
		customerEmail: user.email,
		customerPhone: user.phone_number,
		customerAddress: [user.address, user.apt_number]
			.filter(Boolean)
			.join(" ")
			.trim(),
		customerCity: user.city,
		customerState: user.state?.name,
		customerZipCode: normalizeUsZip5(user.zipcode),
		companyName: params.companyName,
		isBusiness: true,
		customerDateOfBirth: user.date_of_birth,
		client_context: {
			redirect_uri: params.redirectUri,
			source: "iframe",
			session_id: "",
			container_id: "Hello",
		},
	};

	try {
		const response = await axios.post<unknown>(url, sessionPayload, {
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${params.accessToken}`,
			},
			timeout: 30000,
		});

		const data = ZodSentryReporter.parse(SessionResponseSchema, response);

		return data;
	} catch (err) {
		if (!(err instanceof ZodError)) {
			captureException(err, {
				tags: {
					feature: "bank_linking_session",
					handler: "create_session",
				},
				fingerprint: ["bank-linking", "processor-session"],
				extra: { locationId: params.locationId },
			});
		}
		throw err;
	}
}
